
import { Observable, empty as observableEmpty, of as observableOf, Subject } from 'rxjs'

import { useMountEffect } from 'hooks/useMountEffect'
import { Feed, MAX_FEEDS_IN_STREAM, Stream } from 'interfaces'
import { useRef, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { catchError, concatMap, mergeMap } from 'rxjs/operators'

import Button from '@mui/material/Button'
import CreateStreamForm, { FooterState } from 'components/CreateStreamForm/CreateStreamForm'
import PPSwitch from 'components/PPSwitch'
import { PPDrawer } from 'components/PPDrawer'
import { FEATURE_FIND_MY_STREAMS_CREATE, FEATURE_FIND_PRIVATE_STREAMS } from 'shared/constants'
import { ColorGradient } from 'utils/colors'
import numberToHuman from 'utils/format/numberToHuman'

import { mdiClose } from '@mdi/js'
import Icon from '@mdi/react'
import SocialIcon from 'components/SocialIcon'
import { useDispatch } from 'react-redux'
import { useLocation } from 'react-router'
import { setSelectedFeedsToSave } from 'services/content'
import { SnackType } from 'services/snackbar/interfaces/PendingTreat'
import styles from './SaveFeedDialog.pcss'

const CURATED_STREAMS_OWNER_ID = '720'

const modeOptions = [
  { label: 'New stream', value: 'create' },
  { label: 'Existing stream', value: 'save' }
]

export interface SaveFeedDialogProps {
  selectedFeeds: Feed[]
  streams: Array<Stream>
  userId?: string
  createStream: (
    name: string,
    color?: string,
    feeds?: Feed[],
    isPrivate?: boolean,
    file?: File,
    featuredImage?: string,
    secondaryImages?: string[]
  ) => Promise<any>
  addFeeds: (feeds: Feed[], stream: Stream) => Observable<any>
  onClose: () => void
  trackFeedInteraction: (ids: string[]) => Promise<string[]>
  getUserStreams: (force?: boolean) => Promise<Stream[]>
  checkFeatureAvailability: (feature: string) => boolean
  displayNotification: (text: string, type?: SnackType) => void
  removeFeed: (id: string) => Observable<any>
}

export function SaveFeedDialog(props: SaveFeedDialogProps) {
  const intl = useIntl()
  const location = useLocation()
  const dispatch = useDispatch()
  const { selectedFeeds } = props
  const singleFeed = selectedFeeds.length === 1 ? selectedFeeds[0] : null
  const [loading, setLoading] = useState(false)
  const [mode, setMode] = useState<'create' | 'save'>('create')
  const [footerState, setFooterState] = useState<FooterState>({ canSubmit: false, label: null, submit: () => {} })
  const streamToggle$ = useRef<Subject<{ stream: Stream, feeds: Feed[] }>>(undefined)
  const feedImages = selectedFeeds.map(f => f.image).filter(Boolean) as string[]

  useMountEffect(() => {
    streamToggle$.current = new Subject<{ stream: Stream, feeds: Feed[] }>()
    streamToggle$.current.pipe(concatMap(({ stream, feeds }) => {
      if (feeds.length === 0) {
        return observableEmpty()
      }

      if (feeds.length === 1) {
        const feedIndex = stream.feeds.findIndex(f => f.uniqueSource === feeds[0].uniqueSource)
        const isFeedInStream = feedIndex !== -1

        if (isFeedInStream) {
          return props.removeFeed(stream.feeds[feedIndex].id).pipe(catchError(error => {
            console.error('Remove feed failed,', error)
            return observableEmpty()
          }))
        }
      }
      setLoading(true)

      // Add feed to stream
      if (props.userId !== CURATED_STREAMS_OWNER_ID && stream.feedIds.length + feeds.length > MAX_FEEDS_IN_STREAM) {
        props.displayNotification(intl.formatMessage(
          { id: 'find.save-feed-panel.labels.feeds-limit-reached' },
          { count: MAX_FEEDS_IN_STREAM }
        ), 'warning')
        return observableEmpty()
      }

      return props.addFeeds(feeds, stream).pipe(mergeMap(() => {
        if (location.pathname.startsWith('/content/search')) {
          props.trackFeedInteraction(feeds.map(f => f.uniqueSource))
        }
        return observableOf({})
      }),catchError(error => {
        console.error('Add feed failed,', error)
        setLoading(false)
        return observableEmpty()
      }))
    })).subscribe(() => {
      props.displayNotification('Feeds added!', 'success')
      setLoading(false)
      props.onClose()
    })

    return () => {
      if (streamToggle$.current) {
        streamToggle$.current.unsubscribe()
      }
    }
  })

  useMountEffect(() => {
    props.getUserStreams(false)
  })

  if (selectedFeeds.length === 0) {
    return null
  }

  // EXPL: We want to display only owned streams (without "My Profiles")
  const userStreams = props.streams.filter(stream => !stream.originalId && !stream.protected)

  const isPrivateStreamCreateAllowed = () => {
    return props.checkFeatureAvailability(FEATURE_FIND_PRIVATE_STREAMS)
  }

  const createStream = (
    name: string,
    color: ColorGradient,
    imageFile: File | null,
    isPrivate?: boolean,
    featuredImage?: string,
    secondaryImages?: string[]
  ) => {
    const createStreamAllowed = props.checkFeatureAvailability(FEATURE_FIND_MY_STREAMS_CREATE)
    if (!createStreamAllowed) {
      return true
    }

    const colorString = `${color.from},${color.to}`
    setLoading(true)
    // Set default images from feeds, if not selected by user
    const mainImage = featuredImage || feedImages[0]
    /* eslint-disable-next-line no-magic-numbers */
    const defaultSecondaryImages = secondaryImages || feedImages.slice(1, 3).filter(Boolean)
    props.createStream(name, colorString, selectedFeeds, isPrivate, imageFile || undefined, mainImage, defaultSecondaryImages)
      .then(() => props.getUserStreams(true))
      .then(() => {
        setLoading(false)
        props.displayNotification(intl.formatMessage({ id: 'find.save-feed-dialog.notifications.stream-created' }), 'success')
        props.onClose()
      })
      .catch((error: any) => {
        console.error('Error creating stream: ', error)
        props.displayNotification(intl.formatMessage({ id: 'find.save-feed-dialog.notifications.stream-create-error' }), 'error')
        setLoading(false)
      })
    return true
  }

  const removeFeedFromSelection = (id: string) => {
    dispatch(setSelectedFeedsToSave(selectedFeeds.filter(f => f.id !== id)))
  }

  const saveToStream = (feeds: Feed[], str: Stream) => {
    streamToggle$.current?.next({ stream: str, feeds })
    props.onClose()
  }

  const title = (
    <div className={styles['drawer-title']}>
      <span>{singleFeed ? 'Save feed to' : `Save ${selectedFeeds.length} feeds to`}</span>
      <PPSwitch
        options={modeOptions}
        selectedValue={mode}
        onSelectedValueChange={setMode as any}
      />
    </div>
  )

  const footer = (
    <div className={styles['footer-actions']}>
      <Button variant="text" onClick={props.onClose}>
        <FormattedMessage id="actions.cancel" />
      </Button>
      <Button
        variant="contained"
        color="primary"
        className={styles['btn-save']}
        disabled={!footerState.canSubmit}
        onClick={footerState.submit}
      >
        {footerState.label}
      </Button>
    </div>
  )

  return (
    <PPDrawer
      open
      onClose={props.onClose}
      size="md"
      title={title}
      footer={footer}
    >
      <div className={styles.body}>
        <div className={styles.strip}>
          {singleFeed ? (
            <div className={styles['feed-summary']}>
              <SocialIcon outline className={styles['strip-icon']} icon={singleFeed.type} size="20px" />
              <div className={styles['strip-text']}>
                <span className={styles['feed-name']}>{singleFeed.name}</span>
                <p className={styles['feed-type']}>{`${singleFeed.type} feed`}</p>
                {singleFeed.fanCount ? (
                  <p className={styles.followers}>
                    {`${numberToHuman(singleFeed.fanCount)} `}
                    <FormattedMessage id="general.feed.followers" values={{ count: singleFeed.fanCount }} />
                  </p>
                ) : null}
              </div>
            </div>
          ) : (
            <div className={styles['feeds-multi']}>
              <h4 className={styles['feeds-list-title']}>{`Feeds: ${selectedFeeds.length}`}</h4>
              <ul className={styles['feeds-list']}>
                {selectedFeeds.map(feed => (
                  <FeedListItem key={feed.id} feed={feed} onDelete={removeFeedFromSelection} />
                ))}
              </ul>
            </div>
          )}
        </div>
        <CreateStreamForm
          externalChrome
          mode={mode}
          onModeChange={setMode}
          onFooterStateChange={setFooterState}
          loading={loading}
          images={feedImages}
          feeds={props.selectedFeeds}
          streams={userStreams}
          onSaveToStream={saveToStream}
          onSubmit={createStream}
          onCancel={props.onClose}
          isPrivateOptionAllowed={isPrivateStreamCreateAllowed}
        />
      </div>
    </PPDrawer>
  )
}

export function FeedListItem({ feed, onDelete }: { feed: Feed, onDelete: (id: string) => void }) {
  const handleDelete = () => {
    onDelete(feed.id)
  }

  return (
    <li className={styles['feed-li']}>
      <SocialIcon outline className={styles['feed-li-icon']} icon={feed.type} size="16px" />
      <span className={`${styles['feed-li-name']} text-ellipsis`}>{feed.name}</span>
      <span className={styles['feed-li-btn']} onClick={handleDelete}>
        <Icon path={mdiClose} size="16px" />
      </span>
    </li>
  )
}
